import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { CheckCircle2, FileText, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import AIFormFillDialog from './AIFormFillDialog';

const ELEGANT_GOLD = '#C9A961';

const FormRendererBranded = ({ template, onSubmit, initialData = {}, readOnly = false, context = {} }) => {
  const [formData, setFormData] = useState(initialData);
  const [signatures, setSignatures] = useState({});

  const handleFieldChange = (fieldId, value) => {
    setFormData({
      ...formData,
      [fieldId]: value,
    });
  };

  const handleAIFillComplete = (filledData) => {
    // Merge AI-filled data with existing form data
    setFormData(prev => ({ ...prev, ...filledData }));
    toast.success('Form fields populated with AI!');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    const missingFields = template.fields
      .filter(field => field.required && !formData[field.field_id])
      .map(field => field.field_name || field.label);
    
    if (missingFields.length > 0) {
      toast.error(`Please fill required fields: ${missingFields.join(', ')}`);
      return;
    }
    
    onSubmit({ ...formData, signatures });
  };

  const renderField = (field) => {
    const value = formData[field.field_id] || '';

    switch (field.field_type) {
      case 'text':
        return (
          <div key={field.field_id} className="mb-6">
            <Label className="text-white font-semibold mb-2 block">
              {field.field_name || field.label} {field.required && <span className="text-red-400">*</span>}
            </Label>
            <Input
              value={value}
              onChange={(e) => handleFieldChange(field.field_id, e.target.value)}
              placeholder={field.placeholder}
              disabled={readOnly}
              className="bg-white text-black border-2 border-gray-300 focus:border-[#C9A961] h-12 text-lg"
              required={field.required}
            />
          </div>
        );

      case 'textarea':
        return (
          <div key={field.field_id} className="mb-6">
            <Label className="text-white font-semibold mb-2 block">
              {field.field_name || field.label} {field.required && <span className="text-red-400">*</span>}
            </Label>
            <Textarea
              value={value}
              onChange={(e) => handleFieldChange(field.field_id, e.target.value)}
              placeholder={field.placeholder}
              disabled={readOnly}
              className="bg-white text-black border-2 border-gray-300 focus:border-[#C9A961] min-h-[120px] text-lg"
              required={field.required}
              rows={4}
            />
          </div>
        );

      case 'select':
        return (
          <div key={field.field_id} className="mb-6">
            <Label className="text-white font-semibold mb-2 block">
              {field.field_name || field.label} {field.required && <span className="text-red-400">*</span>}
            </Label>
            <Select 
              value={value} 
              onValueChange={(val) => handleFieldChange(field.field_id, val)}
              disabled={readOnly}
            >
              <SelectTrigger className="bg-white text-black border-2 border-gray-300 focus:border-[#C9A961] h-12 text-lg">
                <SelectValue placeholder={field.placeholder || "Select an option"} />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#C9A961]/30">
                {field.options?.map((option) => (
                  <SelectItem key={option} value={option} className="text-black hover:bg-[#C9A961]/20 text-lg">
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'radio':
        return (
          <div key={field.field_id} className="mb-6">
            <Label className="text-white font-semibold mb-3 block">
              {field.field_name || field.label} {field.required && <span className="text-red-400">*</span>}
            </Label>
            <div className="space-y-2 bg-white/10 p-4 rounded-lg border border-[#C9A961]/30">
              {field.options?.map((option) => (
                <label key={option} className="flex items-center gap-3 cursor-pointer hover:bg-white/5 p-2 rounded">
                  <input
                    type="radio"
                    name={field.field_id}
                    value={option}
                    checked={value === option}
                    onChange={(e) => handleFieldChange(field.field_id, e.target.value)}
                    disabled={readOnly}
                    className="w-5 h-5 text-[#C9A961]"
                  />
                  <span className="text-white text-lg">{option}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 'checkbox':
        return (
          <div key={field.field_id} className="mb-6">
            <Label className="text-white font-semibold mb-3 block">
              {field.field_name || field.label} {field.required && <span className="text-red-400">*</span>}
            </Label>
            <div className="space-y-2 bg-white/10 p-4 rounded-lg border border-[#C9A961]/30">
              {field.options?.map((option) => (
                <label key={option} className="flex items-center gap-3 cursor-pointer hover:bg-white/5 p-2 rounded">
                  <input
                    type="checkbox"
                    value={option}
                    checked={Array.isArray(value) ? value.includes(option) : false}
                    onChange={(e) => {
                      const currentValues = Array.isArray(value) ? value : [];
                      const newValues = e.target.checked
                        ? [...currentValues, option]
                        : currentValues.filter(v => v !== option);
                      handleFieldChange(field.field_id, newValues);
                    }}
                    disabled={readOnly}
                    className="w-5 h-5 text-[#C9A961]"
                  />
                  <span className="text-white text-lg">{option}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 'date':
        return (
          <div key={field.field_id} className="mb-6">
            <Label className="text-white font-semibold mb-2 block">
              {field.field_name || field.label} {field.required && <span className="text-red-400">*</span>}
            </Label>
            <div className="relative">
              <Input
                type="date"
                value={value}
                onChange={(e) => handleFieldChange(field.field_id, e.target.value)}
                disabled={readOnly}
                className="bg-white text-black border-2 border-gray-300 focus:border-[#C9A961] h-12 text-lg"
                required={field.required}
              />
              <Calendar className="absolute right-3 top-3 h-6 w-6 text-gray-400 pointer-events-none" />
            </div>
          </div>
        );

      case 'file':
        return (
          <div key={field.field_id} className="mb-6">
            <Label className="text-white font-semibold mb-2 block">
              {field.field_name || field.label} {field.required && <span className="text-red-400">*</span>}
            </Label>
            <Input
              type="file"
              onChange={(e) => handleFieldChange(field.field_id, e.target.files)}
              disabled={readOnly}
              className="bg-white text-black border-2 border-gray-300 focus:border-[#C9A961] h-12 text-lg file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-[#C9A961] file:text-black file:font-semibold hover:file:bg-[#D4C278]"
              multiple
            />
          </div>
        );

      case 'signature':
        return (
          <div key={field.field_id} className="mb-6">
            <Label className="text-white font-semibold mb-2 block">
              {field.field_name || field.label} {field.required && <span className="text-red-400">*</span>}
            </Label>
            <div className="bg-white p-6 rounded-lg border-2 border-[#C9A961] min-h-[150px] flex items-center justify-center">
              <div className="text-center">
                <FileText className="h-12 w-12 text-[#C9A961] mx-auto mb-3" />
                <p className="text-gray-600 text-lg">Signature Canvas</p>
                <p className="text-gray-500 text-sm mt-2">(Digital signature feature coming soon)</p>
                <Input
                  type="text"
                  placeholder="Type your full name"
                  value={signatures[field.field_id] || ''}
                  onChange={(e) => setSignatures({ ...signatures, [field.field_id]: e.target.value })}
                  className="mt-4 text-center font-signature text-2xl"
                  disabled={readOnly}
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!template) {
    return <div className="text-white text-center py-8">No template provided</div>;
  }

  return (
    <div className="max-w-5xl mx-auto bg-gradient-to-br from-gray-900 to-black">
      {/* Company Header */}
      <div className="bg-gradient-to-r from-black via-gray-900 to-black border-b-2 border-[#C9A961] p-8">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-6">
            <img 
              src="/williams-logo-transparent.png" 
              alt="Williams Diversified LLC" 
              className="h-20 w-auto"
            />
            <div>
              <h3 className="text-white text-3xl font-bold">Williams Diversified LLC</h3>
              <p className="text-[#C9A961] text-lg mt-1">Construction Management & Services</p>
              <div className="text-gray-400 text-sm mt-3 space-y-1">
                <p>📍 [Your Business Address Here]</p>
                <p>📧 admin@williamsdiverse.com</p>
                <p>📞 [Your Phone Number]</p>
              </div>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[#C9A961] text-sm font-semibold">Form #{(template.template_id || template.id)?.slice(0, 8).toUpperCase()}</p>
            <p className="text-gray-400 text-sm">Version {template.version || '1.0'}</p>
            <p className="text-gray-400 text-sm mt-1">{new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      {/* Form Title Section */}
      <div className="bg-[#C9A961] px-8 py-6">
        <h2 className="text-black text-3xl font-bold text-center uppercase tracking-wide">
          {template.template_name || template.name}
        </h2>
        {template.description && (
          <p className="text-black/80 text-center mt-2 text-lg">
            {template.description}
          </p>
        )}
      </div>

      {/* Form Content */}
      <form onSubmit={handleSubmit} className="p-8">
        <Card className="bg-black/60 border-2 border-[#C9A961]/50 backdrop-blur-xl p-8">
          {/* Category Badge */}
          {template.category && (
            <div className="mb-6">
              <span className="inline-block bg-[#C9A961]/20 text-[#C9A961] px-4 py-2 rounded-full text-sm font-semibold border border-[#C9A961]/50">
                {template.category.toUpperCase()}
              </span>
            </div>
          )}

          {/* Form Fields */}
          <div className="space-y-2">
            {template.fields?.map((field) => renderField(field))}
          </div>

          {/* Submit Button */}
          {!readOnly && (
            <div className="mt-8 pt-8 border-t-2 border-[#C9A961]/30 flex justify-between items-center gap-4">
              <AIFormFillDialog
                template={template}
                onFillComplete={handleAIFillComplete}
                context={context}
              />
              <div className="flex gap-4">
                <Button
                  type="submit"
                  className="bg-[#C9A961] hover:bg-[#D4C278] text-black font-bold py-6 text-xl transition-all hover:scale-105 px-8"
                >
                  <CheckCircle2 className="h-6 w-6 mr-3" />
                  Submit Form
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="border-2 border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961]/10 py-6 px-8 text-xl"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to cancel? All data will be lost.')) {
                      setFormData({});
                      setSignatures({});
                    }
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </Card>
      </form>

      {/* Form Footer with Company Info */}
      <div className="bg-gradient-to-r from-black via-gray-900 to-black border-t-2 border-[#C9A961] p-6">
        <div className="text-center">
          <p className="text-[#C9A961] font-semibold text-lg">Williams Diversified LLC</p>
          <p className="text-gray-400 text-sm mt-2">
            123 Construction Avenue, City, State 12345
          </p>
          <p className="text-gray-400 text-sm">
            Phone: (555) 123-4567 | Email: info@williamsdiversified.com
          </p>
          <p className="text-gray-500 text-xs mt-3">
            © {new Date().getFullYear()} Williams Diversified LLC. All Rights Reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FormRendererBranded;
